import { supabase } from './supabase';

export type DigestFrequency = 'immediate' | 'daily' | 'weekly' | 'none';

/** Thrown by follow() when a free account hits the watchlist follow cap. */
export const WATCHLIST_LIMIT_CODE = 'WATCHLIST_LIMIT';

export interface DocketFollow {
  id: string;
  user_id: string;
  docket_number: string;
  followed_at: string;
  notify_new_filings: boolean;
  digest_frequency: DigestFrequency;
  last_notified_at: string | null;
}

export interface FollowedDocketWithMeta extends DocketFollow {
  proceeding_name?: string | null;
  status?: string | null;
  bureau?: string | null;
}

class DocketFollowService {
  private static instance: DocketFollowService;
  private followCache: Map<string, boolean> = new Map();
  private cacheTimeout: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  static getInstance(): DocketFollowService {
    if (!DocketFollowService.instance) {
      DocketFollowService.instance = new DocketFollowService();
    }
    return DocketFollowService.instance;
  }

  private getCacheKey(userId: string, docketNumber: string): string {
    return `${userId}:${docketNumber}`;
  }

  private isCacheValid(cacheKey: string): boolean {
    const timestamp = this.cacheTimeout.get(cacheKey);
    return timestamp ? Date.now() < timestamp : false;
  }

  private setCache(cacheKey: string, isFollowing: boolean): void {
    this.followCache.set(cacheKey, isFollowing);
    this.cacheTimeout.set(cacheKey, Date.now() + this.CACHE_DURATION);
  }

  clearAllCache(): void {
    this.followCache.clear();
    this.cacheTimeout.clear();
  }

  async isFollowing(docketNumber: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const cacheKey = this.getCacheKey(user.id, docketNumber);
      if (this.followCache.has(cacheKey) && this.isCacheValid(cacheKey)) {
        return this.followCache.get(cacheKey)!;
      }

      const { data, error } = await supabase
        .from('docket_follows')
        .select('id')
        .eq('user_id', user.id)
        .eq('docket_number', docketNumber)
        .maybeSingle();

      if (error) {
        console.error('Error checking follow status:', error);
        return false;
      }

      const result = !!data;
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error checking follow status:', error);
      return false;
    }
  }

  async follow(docketNumber: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('docket_follows')
      .insert({
        user_id: user.id,
        docket_number: docketNumber,
      });

    if (error) {
      if (error.code === '23505') return true; // already following
      // The DB trigger raises this when a free account is at its follow cap.
      if (typeof error.message === 'string' && error.message.includes('watchlist_follow_limit_reached')) {
        const limitErr = new Error('watchlist_follow_limit_reached') as Error & { code?: string };
        limitErr.code = WATCHLIST_LIMIT_CODE;
        throw limitErr;
      }
      throw error;
    }

    const cacheKey = this.getCacheKey(user.id, docketNumber);
    this.setCache(cacheKey, true);
    return true;
  }

  /** Count of the current user's personal follows (excludes firm-provisioned). */
  async getFollowCount(): Promise<number> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;
    const { count } = await supabase
      .from('docket_follows')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .is('firm_id', null);
    return count ?? 0;
  }

  async unfollow(docketNumber: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('docket_follows')
      .delete()
      .eq('user_id', user.id)
      .eq('docket_number', docketNumber);

    if (error) throw error;

    const cacheKey = this.getCacheKey(user.id, docketNumber);
    this.setCache(cacheKey, false);
    return false;
  }

  async toggleFollow(docketNumber: string): Promise<boolean> {
    const current = await this.isFollowing(docketNumber);
    return current ? this.unfollow(docketNumber) : this.follow(docketNumber);
  }

  async getFollowedDockets(): Promise<FollowedDocketWithMeta[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: follows, error } = await supabase
      .from('docket_follows')
      .select('*')
      .eq('user_id', user.id)
      .order('followed_at', { ascending: false });

    if (error) {
      console.error('Error fetching followed dockets:', error);
      throw error;
    }

    if (!follows || follows.length === 0) return [];

    const docketNumbers = follows.map(f => f.docket_number);
    const { data: proceedings } = await supabase
      .from('open_proceedings')
      .select('docket_number, proceeding_name, status, bureau')
      .in('docket_number', docketNumbers);

    const procMap = new Map(
      (proceedings || []).map(p => [p.docket_number, p])
    );

    return follows.map(f => ({
      ...f,
      proceeding_name: procMap.get(f.docket_number)?.proceeding_name ?? null,
      status: procMap.get(f.docket_number)?.status ?? null,
      bureau: procMap.get(f.docket_number)?.bureau ?? null,
    }));
  }

  async getFollowStatus(docketNumber: string): Promise<DocketFollow | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('docket_follows')
      .select('*')
      .eq('user_id', user.id)
      .eq('docket_number', docketNumber)
      .maybeSingle();

    if (error) {
      console.error('Error fetching follow status:', error);
      return null;
    }

    return data;
  }

  async updateDigestFrequency(
    docketNumber: string,
    frequency: DigestFrequency
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('docket_follows')
      .update({
        digest_frequency: frequency,
        notify_new_filings: frequency !== 'none',
      })
      .eq('user_id', user.id)
      .eq('docket_number', docketNumber);

    if (error) throw error;
  }

  async updateNotifyNewFilings(
    docketNumber: string,
    notify: boolean
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('docket_follows')
      .update({ notify_new_filings: notify })
      .eq('user_id', user.id)
      .eq('docket_number', docketNumber);

    if (error) throw error;
  }

  async getAllFollowedDocketNumbers(): Promise<Set<string>> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return new Set();

    const { data, error } = await supabase
      .from('docket_follows')
      .select('docket_number')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error fetching followed docket numbers:', error);
      return new Set();
    }

    const set = new Set<string>();
    (data || []).forEach(row => {
      if (row.docket_number) {
        set.add(row.docket_number);
        const cacheKey = this.getCacheKey(user.id, row.docket_number);
        this.setCache(cacheKey, true);
      }
    });

    return set;
  }
}

export const docketFollowService = DocketFollowService.getInstance();
